import { FC } from 'react';
import clsx from 'clsx';
import { RenderIcons } from './RenderIcons';
import { StepNumberCircle } from './StepNumberCircle';
import { MenuConfig } from 'alurkerja-ui';
import { RxDotFilled } from 'react-icons/rx';
import { matchPath, useLocation } from 'react-router-dom'; // 🧩 tambahkan ini
import useThemeState from '@/states/theme.state';
import { StepStatus } from '@/components/pages/dashboard/dashboard.types';

interface ExtendedMenuConfig extends MenuConfig {
  disabled?: boolean;
  stepNumber?: number;
  stepStatus?: StepStatus;
}

const BRAND_DARK = '#071753';
const IN_PROGRESS_ORANGE = '#FD6118';

const Menu: FC<{
  menu: MenuConfig;
  toggled: boolean;
  menuWrapper?: ({
    children,
    menu,
    toggled,
  }: {
    children: JSX.Element;
    menu: MenuConfig;
    toggled: boolean;
  }) => JSX.Element;
  isChildren?: boolean;
}> = ({ menu, toggled, menuWrapper, isChildren }) => {
  const { pathname } = useLocation();
  const isDark = useThemeState(s => s.mode === 'dark');
  const activeColor = isDark ? IN_PROGRESS_ORANGE : BRAND_DARK;

  const isDisabled = (menu as ExtendedMenuConfig).disabled || false;

  const isActive = (() => {
    if (!pathname || !menu.href) return false;

    return !!matchPath({ path: menu.href, end: false }, pathname);
  })();

  const stepMenu = menu as ExtendedMenuConfig;
  const hasStepNumber = typeof stepMenu.stepNumber === 'number';

  // AOS steps show a numbered circle (with a check badge when done); all other
  // menu items keep their original icon.
  const leadingVisual = hasStepNumber ? (
    <StepNumberCircle number={stepMenu.stepNumber as number} status={stepMenu.stepStatus ?? 'not_started'} />
  ) : (
    <span className="text-sm">
      <RenderIcons icon={menu.icon} fallback={<RxDotFilled />}></RenderIcons>
    </span>
  );

  const RenderMenu = (
    <div
      className={clsx(
        'cursor-pointer font-medium px-4 rounded-md flex items-center w-full whitespace-nowrap gap-x-2 mb-0 min-h-10 text-sm',
        isDisabled && 'opacity-50 cursor-not-allowed pointer-events-none',
      )}
    >
      <div
        className={clsx(
          'flex items-center py-2 w-full gap-x-2 rounded-lg px-3',
          !isDisabled && 'hover:bg-[#ECF0F7] dark:hover:bg-gray-700',
          isActive
            ? 'font-semibold bg-[#ECF0F7] dark:bg-gray-700 dark:text-blue-400'
            : 'text-[#5E5E5E] dark:text-gray-300',
        )}
        style={isActive ? { color: activeColor } : undefined}
      >
        {(!isChildren || toggled) && leadingVisual}

        {!toggled && <span className="whitespace-normal">{menu.label}</span>}
        {!toggled && menu.count && (
          <div
            className={clsx(
              'flex items-center justify-center ml-auto text-xs rounded p-1 text-[#414651] dark:text-gray-200 font-medium',
              isActive ? 'bg-white dark:bg-gray-600' : 'bg-[#EDEDED] dark:bg-gray-700',
            )}
          >
            {menu.count || 0}
          </div>
        )}
      </div>
    </div>
  );

  if (menuWrapper) {
    return menuWrapper({ children: RenderMenu, menu: menu, toggled: toggled });
  }

  if (isDisabled) {
    return (
      <div
        className={clsx(
          'font-medium px-6 rounded-md flex items-center w-full whitespace-nowrap gap-x-2 mb-0 h-10 text-sm',
          'text-[#5E5E5E] dark:text-gray-400 opacity-50 cursor-not-allowed',
        )}
      >
        <div className="flex items-center py-2 w-full gap-x-2">
          {(!isChildren || toggled) && (
            <span className="text-sm">
              <RenderIcons icon={menu.icon} fallback={<RxDotFilled />}></RenderIcons>
            </span>
          )}
          {!toggled && <span className="whitespace-normal">{menu.label}</span>}
          {!toggled && menu.count && (
            <div className="flex items-center justify-center ml-auto text-xs rounded bg-[#EDEDED] p-1 text-[#414651] font-medium">
              {menu.count || 0}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <a
      href={menu.href}
      className={clsx(
        'cursor-pointer font-medium px-6 rounded-md flex items-center w-full whitespace-nowrap gap-x-2 hover:bg-[#ECF0F7] dark:hover:bg-gray-700 mb-0 h-10 text-sm',
        isActive
          ? 'font-semibold bg-[#ECF0F7] dark:bg-gray-700 dark:text-blue-400'
          : 'text-[#5E5E5E] dark:text-gray-300',
      )}
      style={isActive ? { color: activeColor } : undefined}
    >
      <div className="flex items-center py-2 w-full gap-x-2">
        {(!isChildren || toggled) && leadingVisual}
        {!toggled && <span className="whitespace-normal">{menu.label}</span>}
        {!toggled && menu.count && (
          <div
            className={clsx(
              'flex items-center justify-center ml-auto text-xs rounded p-1 text-[#414651] dark:text-gray-200 font-medium',
              isActive ? 'bg-white dark:bg-gray-600' : 'bg-[#EDEDED] dark:bg-gray-700',
            )}
          >
            {menu.count || 0}
          </div>
        )}
      </div>
    </a>
  );
};

export default Menu;
